import { invariant } from "Global/Base/Utils/AssertUtils";
import { ILogger } from "./ILogger";
import { ISectionLogger } from "./ISectionLogger";
import {
    FineLevelOffset,
    LoggingLevel,
    LoggingLevelType,
    LoggingMessage,
    LoggingMessageOrGenerator,
    LoggingSectionType,
    PendingGroup,
} from "./types";
import { createPendingGroup, substituteArguments } from "./utils";

/**
 * Hlavní (defaultní) implementace {@link ISectionLogger}. Obálka nad {@link ILogger}.
 * Zná sekci a práh logování.
 * @see LazyLoadingSectionLogger
 */
export class SectionLogger implements ISectionLogger {
    /**
     * Oblast, které se tento logger týká.
     */
    private readonly _section: LoggingSectionType;

    /**
     * Práh logování. Zprávy s nižší závažností budou zahozeny.
     */
    private readonly _levelThreshold: LoggingLevelType;

    /**
     * Logujeme tímto loggerem.
     */
    private readonly _logger: ILogger;

    /**
     * Počet volání {@link group} a {@link groupCollapsed} bez odpovídajících provolání {@link groupEnd}.
     */
    private _groupsDeep: number;

    /**
     * Informace potřebné pro pozdější provedení příkazu {@link group} nebo {@link groupCollapsed}.
     * Viz {@link ISectionLogger.group}.
     */
    private _pendingGroups: PendingGroup[] = [];

    constructor(section: LoggingSectionType, levelThreshold: LoggingLevelType, logger: ILogger) {
        this._section = section;
        this._levelThreshold = levelThreshold;
        this._logger = logger;
        this._groupsDeep = 0;
    }

    public error = this._createLogIfEnabled(LoggingLevel.error);
    public warn = this._createLogIfEnabled(LoggingLevel.warning);
    public info = this._createLogIfEnabled(LoggingLevel.info);
    public log = this._createLogIfEnabled(LoggingLevel.info);
    public debug = this._createLogIfEnabled(LoggingLevel.debug);

    public fineLog(mainLevel: LoggingLevel, fineLevelOffset: FineLevelOffset, messageOrGenerator: LoggingMessage | (() => LoggingMessage), ...args: any[]): void {
        const fineLevel = mainLevel + fineLevelOffset;
        this._logIfEnabled(fineLevel, messageOrGenerator, ...args);
    }

    public logException(exception: Error): void {
        this._logExceptionIfEnabled(LoggingLevel.error, exception);
    }

    public fineLogException(mainLevel: LoggingLevel, fineLevelOffset: FineLevelOffset, exception: Error): void {
        const fineLevel = mainLevel + fineLevelOffset;
        this._logExceptionIfEnabled(fineLevel, exception);
    }

    public group(messageOrGenerator: LoggingMessageOrGenerator, ...args: any[]): void {
        this._groupsDeep += 1;
        this._pendingGroups.push(createPendingGroup(messageOrGenerator, args, false));
    }

    public groupCollapsed(messageOrGenerator: LoggingMessageOrGenerator, ...args: any[]): void {
        this._groupsDeep += 1;
        this._pendingGroups.push(createPendingGroup(messageOrGenerator, args, true));
    }

    public groupEnd(): void {
        invariant(this._groupsDeep > 0, "`groupEnd` was called more times than group+groupCollapsed.");
        if (this._pendingGroups.length > 0) {
            this._pendingGroups.pop();
        } else {
            this._logger.groupEnd();
        }
        this._groupsDeep -= 1;
    }

    private _handlePendingGroups(): void {
        this._pendingGroups.forEach(({ messageOrGenerator, args, isCollapsed }) => {
            const message = (typeof messageOrGenerator === "function") ? messageOrGenerator() : messageOrGenerator;
            const { substitutedMessage, unsubstitutedArguments } = substituteArguments(message, ...args);
            isCollapsed
                ? this._logger.groupCollapsed(this._section, substitutedMessage, ...unsubstitutedArguments)
                : this._logger.group(this._section, substitutedMessage, ...unsubstitutedArguments);
        });
        this._pendingGroups = [];
    }

    /**
     * Vrátí funkci, která loguje se zadanou závažností. S tím, že pokud je daná závažnost mimo stanovený práh, pak nic neprovede.
     * @param level Závažnost, se kterou bude vrácená funkce logovat.
     */
    private _createLogIfEnabled(level: LoggingLevel): (messageOrGenerator: LoggingMessageOrGenerator, ...args: any[]) => void {
        return ((messageOrGenerator: LoggingMessageOrGenerator, ...args: any[]) => this._logIfEnabled(level, messageOrGenerator, ...args));
    }

    /**
     * Zaloguje se zadanou závažností. S tím, že pokud je daná závažnost mimo stanovený práh, pak nic neprovede.
     */
    private _logIfEnabled(level: LoggingLevelType, messageOrGenerator: LoggingMessageOrGenerator, ...args: any[]): void {
        if (this._isLogEnabled(level)) {
            this._handlePendingGroups();
            const message = (typeof messageOrGenerator === "function") ? messageOrGenerator() : messageOrGenerator;
            const { substitutedMessage, unsubstitutedArguments } = substituteArguments(message, ...args);
            this._logger.log(this._section, level, substitutedMessage, ...unsubstitutedArguments);
        }
    }

    /**
     * Zaloguje výjimku se zadanou závažností. S tím, že pokud je daná závažnost mimo stanovený práh, pak nic neprovede.
     */
    private _logExceptionIfEnabled(level: LoggingLevelType, exception: Error): void {
        if (this._isLogEnabled(level)) {
            this._handlePendingGroups();
            this._logger.logException(this._section, level, exception);
        }
    }

    private _isLogEnabled(level: LoggingLevelType): boolean {
        return (level <= this._levelThreshold);
    }
}
